import { useState, useEffect, useCallback } from "react";
import { api } from "../App";
import DOMPurify from "dompurify";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";
import { Bell, Calendar, Image as ImageIcon, ChevronDown, ChevronUp, Heart } from "lucide-react";
import Layout from "../components/Layout";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_social-circle-37/artifacts/vzzvahrb_IMG-20251227-WA0000.jpg";

const UpdatesPage = () => {
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedUpdates, setExpandedUpdates] = useState({});
  const [likedUpdates, setLikedUpdates] = useState({});
  const [likeCounts, setLikeCounts] = useState({});
  const [likeLoading, setLikeLoading] = useState({});

  const fetchUpdates = useCallback(async () => {
    try {
      const response = await api.get("/updates");
      setUpdates(response.data);
      
      // Fetch like status for each update
      const likeStatuses = {};
      const counts = {};
      for (const update of response.data) {
        try {
          const likeRes = await api.get(`/updates/${update.id}/likes`);
          likeStatuses[update.id] = likeRes.data.has_liked;
          counts[update.id] = likeRes.data.likes_count;
        } catch (_e) {
          likeStatuses[update.id] = false;
          counts[update.id] = update.likes?.length || 0;
        }
      }
      setLikedUpdates(likeStatuses);
      setLikeCounts(counts);
    } catch (_error) {
      toast.error("Failed to load updates");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUpdates();
  }, [fetchUpdates]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const toggleExpand = (updateId) => {
    setExpandedUpdates(prev => ({
      ...prev,
      [updateId]: !prev[updateId]
    }));
  };

  const handleLike = async (updateId) => {
    if (likedUpdates[updateId]) {
      // Already liked, cannot unlike
      return;
    }

    setLikeLoading(prev => ({ ...prev, [updateId]: true }));
    
    try {
      const response = await api.post(`/updates/${updateId}/like`);
      if (response.data.success) {
        setLikedUpdates(prev => ({ ...prev, [updateId]: true }));
        setLikeCounts(prev => ({ ...prev, [updateId]: response.data.likes_count }));
        toast.success("Update liked!");
      } else {
        toast.info(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to like update");
    } finally {
      setLikeLoading(prev => ({ ...prev, [updateId]: false }));
    }
  };

  const isNewUpdate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffHours = (now - date) / (1000 * 60 * 60);
    return diffHours < 24;
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-background p-4">
          <div className="max-w-2xl mx-auto space-y-4">
            {[1, 2, 3].map(i => (
              <Card key={i}>
                <CardHeader>
                  <Skeleton className="h-6 w-3/4" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-4 w-full mb-2" />
                  <Skeleton className="h-4 w-5/6" />
                  <Skeleton className="h-32 w-full mt-4" />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-background relative">
        {/* Watermark */}
        <div className="fixed inset-0 pointer-events-none z-0 flex items-center justify-center">
          <img 
            src={LOGO_URL} 
            alt="Lunawat Foundation" 
            className="w-64 h-64 md:w-96 md:h-96 object-contain opacity-5"
          />
        </div>

        <div className="relative z-10 p-4 pb-32">
          {/* Header */}
          <div className="max-w-2xl mx-auto mb-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Bell className="w-5 h-5 text-primary" />
              </div>
              <h1 className="text-2xl font-bold">Updates</h1>
            </div>
            <p className="text-muted-foreground text-sm">
              Latest announcements and updates from LUNAWAT FOUNDATION
            </p>
          </div>

          {/* Updates List */}
          <div className="max-w-2xl mx-auto space-y-4">
            {updates.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <Bell className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                  <h3 className="font-semibold text-lg mb-2">No Updates Yet</h3>
                  <p className="text-muted-foreground text-sm">
                    Check back later for the latest announcements and updates.
                  </p>
                </CardContent>
              </Card>
            ) : (
              updates.map((update) => {
                const isExpanded = expandedUpdates[update.id];
                const hasImages = update.images && update.images.length > 0;
                const isNew = isNewUpdate(update.created_at);
                const hasLiked = likedUpdates[update.id];
                const likeCount = likeCounts[update.id] || 0;
                const isLiking = likeLoading[update.id];

                return (
                  <Card key={update.id} className="overflow-hidden animate-fade-in">
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <CardTitle className="text-lg flex items-center gap-2">
                            {update.title}
                            {isNew && (
                              <Badge variant="default" className="text-xs">
                                New
                              </Badge>
                            )}
                          </CardTitle>
                          <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                            <Calendar className="w-3 h-3" />
                            {formatDate(update.created_at)}
                            {hasImages && (
                              <>
                                <span>•</span>
                                <ImageIcon className="w-3 h-3" />
                                {update.images.length} image{update.images.length > 1 ? 's' : ''}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    
                    <CardContent className="pt-0">
                      {/* Content with HTML rendering */}
                      <div 
                        className={`prose prose-sm max-w-none dark:prose-invert ${
                          !isExpanded && update.content.length > 300 ? 'line-clamp-4' : ''
                        }`}
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(update.content) }}
                      />
                      
                      {/* Show More/Less button */}
                      {update.content.length > 300 && (
                        <button
                          onClick={() => toggleExpand(update.id)}
                          className="flex items-center gap-1 text-primary text-sm mt-2 hover:underline"
                        >
                          {isExpanded ? (
                            <>
                              Show Less <ChevronUp className="w-4 h-4" />
                            </>
                          ) : (
                            <>
                              Read More <ChevronDown className="w-4 h-4" />
                            </>
                          )}
                        </button>
                      )}

                      {/* Images */}
                      {hasImages && (
                        <div className={`mt-4 ${update.images.length === 1 ? '' : 'grid grid-cols-2 gap-2'}`}>
                          {update.images.map((image, index) => (
                            <div 
                              key={`img-${image.slice(-20)}-${index}`} 
                              className={`rounded-lg overflow-hidden bg-muted ${
                                update.images.length === 1 ? 'max-h-96' : 'aspect-square'
                              }`}
                            >
                              <img 
                                src={image} 
                                alt={`Update image ${index + 1}`}
                                className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
                                onClick={() => window.open(image, '_blank')}
                              />
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Like Button */}
                      <div className="mt-4 pt-3 border-t flex items-center gap-3">
                        <Button
                          variant={hasLiked ? "default" : "outline"}
                          size="sm"
                          onClick={() => handleLike(update.id)}
                          disabled={hasLiked || isLiking}
                          className={`gap-2 ${hasLiked ? 'bg-red-500 hover:bg-red-500 text-white' : ''}`}
                          data-testid={`like-btn-${update.id}`}
                        >
                          <Heart className={`w-4 h-4 ${hasLiked ? 'fill-current' : ''}`} />
                          {hasLiked ? 'Liked' : 'Like'}
                        </Button>
                        <span className="text-sm text-muted-foreground">
                          {likeCount} {likeCount === 1 ? 'like' : 'likes'}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default UpdatesPage;
